import React, { useEffect, useState, useMemo } from "react";
import Accordion from "react-bootstrap/Accordion";
import T from "./Translate/T";
import { useTranslate } from "../context/TranslateContext";

const FAQs = () => {
  const { currentLang, translateBatch, isEnglish } = useTranslate();
  const [translatedFaqs, setTranslatedFaqs] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // useMemo keeps faqsData stable — no re-creation on every render
  const faqsData = useMemo(() => [
    {
      question: "What services do you offer?",
      answer:
        "I offer full stack web development services including PHP/Laravel API architecture, React and Next.js front-end development, database design, AWS cloud infrastructure, and legacy system migration. I also provide technology consultancy and business analysis support.",
    },
    {
      question: "What is your development process?",
      answer:
        "I follow an agile methodology with clear communication at every stage. This includes initial requirements gathering, wireframe creation, iterative development sprints, code reviews, testing, and deployment. You'll have visibility into progress throughout the project.",
    },
    {
      question: "What technologies do you specialize in?",
      answer:
        "My core stack includes PHP, Laravel, React, Next.js, TypeScript, MySQL, PostgreSQL, and AWS services. I also have extensive experience with RESTful APIs, microservices architecture, Docker, Codespaces, and CI/CD pipelines.",
    },
    {
      question: "Do you work with international clients?",
      answer:
        "Yes! I'm bilingual (English/Spanish) and have worked with clients across North America, Latin America, and internationally. I'm experienced in remote collaboration and can adapt to different time zones and communication preferences.",
    },
    {
      question: "What is your experience with enterprise projects?",
      answer:
        "I have 10+ years of experience delivering enterprise-grade platforms for clients including Scotiabank, TELUS, Aviva, and Air Miles. I specialize in building scalable, secure solutions that meet enterprise requirements and compliance standards.",
    },
  ], []);

  useEffect(() => {
    // Reset to English immediately while translation loads
    if (isEnglish) {
      setTranslatedFaqs(faqsData);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const translateFaqs = async () => {
      try {
        // Build flat array: [q0, a0, q1, a1, q2, a2, ...]
        const flat = faqsData.flatMap((faq) => [faq.question, faq.answer]);

        // translateBatch returns { 0: text, 1: text, ... } keyed by original index
        const results = await translateBatch(flat, currentLang);

        if (cancelled) return;

        // Reconstruct FAQ objects from the returned index-keyed object
        const translated = faqsData.map((faq, i) => ({
          ...faq,
          question: results[i * 2]     ?? faq.question,
          answer:   results[i * 2 + 1] ?? faq.answer,
        }));

        setTranslatedFaqs(translated);
      } catch (error) {
        console.error("FAQ translation error:", error);
        if (!cancelled) setTranslatedFaqs(faqsData);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    translateFaqs();

    // Cleanup: if language changes again before this resolves, discard stale result
    return () => { cancelled = true; };
  }, [currentLang, isEnglish, faqsData, translateBatch]);

  const displayFaqs = translatedFaqs ?? faqsData;

  return (
    <section id="faq" className="section bg-light">
      <div className="container">
        <div className="row gy-5">
          <div className="col-lg-6 order-1 order-lg-0 wow fadeInUp">
            {/* Heading */}
            <p className="text-center text-lg-start mb-2">
              <span className="bg-primary text-dark px-2">FAQ</span>
            </p>
            <h2 className="text-10 fw-600 text-center text-lg-start mb-5">
              <T>Have any questions?</T>
            </h2>
            {/* Heading end */}

            <Accordion flush defaultActiveKey="0">
              {displayFaqs.map((faq, index) => (
                <Accordion.Item
                  eventKey={String(index)}
                  key={index}
                  className={isLoading ? "faq-loading" : ""}
                >
                  <Accordion.Header>{faq.question}</Accordion.Header>
                  {/* Plain text — no dangerouslySetInnerHTML needed */}
                  <Accordion.Body>{faq.answer}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>

          <div
            className="col-lg-6 d-flex align-items-center justify-content-center order-0 order-lg-1 wow fadeIn"
            data-wow-delay="1s"
          >
            <img
              className="img-fluid"
              src="images/faq.png"
              title="FAQ"
              alt="Frequently asked questions illustration"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQs;
